import { CustomState } from "@hardware/Store/Reducers";
import setFieldValue from "@hardware/Store/State/Survey/SetFieldValue";
import { mediaQueriesBP } from "@hardware/Utilities/variables";
import { SurveyFormContext } from "@hardware/Widgets/Survey/SurveyForm";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Radio, { RadioComponentProps, RadioStyle } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupComponentProps } from "@insite/mobius/RadioGroup";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    radioLabel1 = "radioLabel1",
    radioLabel2 = "radioLabel2",
    checkboxLabel3 = "checkboxLabel3",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.radioLabel1]: string;
        [fields.radioLabel2]: string;
        [fields.checkboxLabel3]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: CustomState) => ({
    canContact:  state.surveyForm.fieldValues["canContact"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CanContactStyles {
    catalogLinesTextField?: TextFieldPresentationProps;
    title?: TypographyPresentationProps;
    checkboxwrapper?: InjectableCss;
    checkboxstyle?: InjectableCss;
    checkbox2style?: InjectableCss;
}

export const catalogLinesStyles: CanContactStyles = {
    title: {
        variant: "p",
        css: css`
            font-size: 15px;
            font-weight: 600;

            &.required::after {
                content: "*";
                margin-left: 3px;
            }
        `,
    },
    checkboxwrapper: {
        css: css`
            display: flex;
            justify-content: flex-start;
            margin: 0 -15px 15px;
            flex-wrap: wrap;
            padding: 0 15px;
            flex-direction: column;
            align-items: baseline;

            ${mediaQueriesBP.maxMD} {
                flex-direction: column;
                align-items: baseline;
            }

            & legend {
               margin-bottom: 0 !important;
            }
        `,
    },
    checkboxstyle: {
        css: css`
            margin-top: 0 !important;
            width: calc(33.33%);
            ${mediaQueriesBP.maxMD} {
                width: 100%;
                padding-bottom: 5px;
            }
        `,
    },
};

const styles = catalogLinesStyles;

const CanContact: React.FC<Props> = ({ fields, setFieldValue, canContact }) => {
    const { title, radioLabel1, radioLabel2, isRequired } = fields;
    const surveyFormContext = React.useContext(SurveyFormContext);    
    const [isContactSales, setIsContactSales] = React.useState(false);
    const [canContactMessage, setcanContactMessage] = React.useState("");


    React.useEffect(() => {
        surveyFormContext.validators.canContact = validateCanContact;
    }, [isRequired]);

    const validateCanContact = () => {
        const errorMessage = isRequired && isContactSales ? (siteMessage("Field_Required", title) as string) : "";
        setcanContactMessage(errorMessage);
        return !errorMessage;
    };


    const handleChangeContactSales = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsContactSales(true);
        setFieldValue({
            key: "canContact",
            value: event.currentTarget.value,
            onComplete: () => {
                setIsContactSales(false);
            },
        });
    };

    return (
        <StyledWrapper>
            <Typography {...styles.title} className={isRequired ? "required" : ""}>{title}</Typography>
            <RadioGroup {...styles.checkboxwrapper}
                 value={canContact}
                 onChangeHandler={handleChangeContactSales}
                 error={canContactMessage}
            >
                <Radio
                    {...styles.checkboxstyle}
                        value="Yes"
                        disabled={isContactSales}
                        data-test-selector="contactMethod_yes"
                >
                    {radioLabel1}
                </Radio>
                <Radio
                    {...styles.checkboxstyle}
                    value="No"
                    disabled={isContactSales}
                    data-test-selector="contactMethod_no"
                >
                    {radioLabel2}
                </Radio>
            </RadioGroup>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CanContact),
    definition: {
        group: "Survey" as any,
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Would you like a representative to contact you?",
                isRequired: true,
            },
            {
                name: fields.radioLabel1,
                displayName: "Checkbox Label 1",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Yes",
                isRequired: true,
            },
            {
                name: fields.radioLabel2,
                displayName: "Checkbox Label 2",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "No",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
